import config from './config';

export type EndOfContractErrorCode =
  | 'INVALID_BODY'
  | 'EFFECTIVE_DATE_TOO_SOON'
  | 'NOT_BELGIUM'
  | 'COMPANY_DATA_INCOMPLETE'
  | 'DGO_UNRESOLVED'
  | 'NO_SDP'
  | 'NO_ACTIVE_CONTRACT'
  | 'EOC_ALREADY_IN_FLIGHT'
  // 409 (commodity): the backend rejected the linked flex contract ids sent
  // with the request (stale portfolio / tampered request).
  | 'LINKED_FLEX_INVALID'
  // 409 (commodity): a single-direction end was requested but the EAN's grid
  // operator does not allow directional EOC.
  | 'DIRECTION_NOT_ALLOWED'
  // 409 (commodity): the connection point is physically under construction —
  // authoritative backstop for the FE eligibility filter. NOT retryable-as-
  // success.
  | 'UNDER_CONSTRUCTION'
  // 409 (commodity): the contract already has an end date set (post-loss or a
  // non-EOC end) — the leg is ALREADY ENDING. Authoritative backstop for the
  // FE "already ending" eligibility filter. NOT retryable-as-success.
  | 'CONTRACT_ALREADY_ENDING'
  // 409 (commodity, FT-5702): the explicit *_contract_id sent for a leg failed
  // the backend's ownership validation — it does not resolve under this
  // EAN+company, its direction does not match the leg it was sent for, its
  // installation does not match that leg's SDP, or the leg is not one the
  // request is ending. Means the FE's portfolio view is stale (or the request
  // was tampered with). NO write, NO Atrias send, NO ledger row happened.
  // Per-contract, NOT systemic — see isSystemicSubmitError.
  | 'CONTRACT_ID_MISMATCH'
  // 409 (commodity, FT-5702): a `future` contract whose requested end date
  // precedes its own start date. Per-contract, NOT systemic.
  | 'END_DATE_BEFORE_START'
  | 'ATRIAS_SEND_FAILED'
  | 'ATRIAS_UNAVAILABLE'
  // Flex-path codes (the 502/503 Atrias codes are unreachable on the flex path):
  | 'NO_FLEX_CONTRACT'
  | 'NOT_LATEST_CONTRACT'
  | 'NOT_ACTIVE'
  | 'ALREADY_ENDING';

export class EndOfContractError extends Error {
  status: number;
  code: EndOfContractErrorCode | null;

  constructor(status: number, message: string, code: EndOfContractErrorCode | null = null) {
    super(message);
    this.name = 'EndOfContractError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Discriminated by contractType:
 * - 'commodity' — injection/offtake contract, identified by EAN.
 * - 'flex' — BESS / PV curtailment / e-boiler contract, identified by
 *   contract_flex_id (the portfolio payload carries no flex EAN; the backend
 *   resolves the flex SDP from the contract id).
 *
 * Semantics differ per kind:
 * - 'commodity' — request-only: the endpoint SENDS an EOC request to Atrias
 *   and writes NO end date. The contract is ended later (yuso-mig6-ws) when
 *   the Atrias loss message is received, which also cascade-ends any flex
 *   contract on the same site.
 * - 'flex' — ended directly by this endpoint: the backend sets the flex
 *   contract's end date to effectiveDate. No Atrias involvement.
 *
 * Cross-repo contract with yuso-portals-backend (FT-4390) — coordinate any
 * shape change with the backend before shipping.
 */
export type EndOfContractRequestBody =
  | {
      contractType: 'commodity';
      company_id: number;
      ean: string;
      effectiveDate: string; // YYYY-MM-DD
      /**
       * contract_flex_ids of the flex contracts linked to THIS commodity
       * (per-commodity linkage, not the batch union), mapped by asset type.
       * The backend persists them so mig6-ws cascade-ends them on loss
       * approval. When injection and offtake both link the same flex, both
       * POSTs deliberately carry the same id (BE/mig6-ws dedupe via the
       * end_date guard) — do not dedupe FE-side.
       */
      bess_contract_id?: number | null;
      eboiler_contract_id?: number | null;
      pv_contract_id?: number | null;
      /**
       * FT-5702 — the COMMODITY contract row the FE actually displayed and the
       * user actually ticked, per leg (`/allservices` `contract_id`, resolved
       * BE-side by `mostRelevantContractIdSubquery` since #511).
       *
       * Why they exist: without them the backend re-resolves each leg by its
       * own `latest = 1` predicate, which can land on a DIFFERENT contract row
       * than the one the portfolio showed (a stale ended sibling sitting at
       * `latest = 1` while the genuinely current contract sits at `latest = 0`).
       * The user then gets a 409 "already ending" for a contract they are not
       * looking at. Sending the id makes the backend end exactly the row the
       * FE displayed.
       *
       * Semantics (wire contract with yuso-portals-backend):
       * - OMITTED or null → that leg resolves by `latest = 1`. Byte-identical
       *   legacy behaviour, so this is backward compatible in both directions.
       * - present with an integer → that leg resolves by `cih.id = ?` /
       *   `coh.id = ?` instead.
       * - present but not an integer → 400 INVALID_BODY.
       * - ownership validation fails → 409 CONTRACT_ID_MISMATCH (no write, no
       *   Atrias send, no ledger row).
       *
       * Which field for which leg — ids are sent ONLY for legs actually being
       * ended: `direction: 'offtake'` → offtake only, `'injection'` →
       * injection only, `'both'`/omitted → both, for whichever legs exist.
       * Sending an id for a leg that is not being ended is a 409.
       *
       * snake_case at the TOP LEVEL of the body, deliberately: it matches the
       * sibling bess_/eboiler_/pv_contract_id convention on this same body and
       * the `mig6_eoc_requests` column names. Do not camelCase or nest.
       */
      offtake_contract_id?: number | null;
      injection_contract_id?: number | null;
      /**
       * Directional EOC for combined (SC_OFFINJE) EANs on a non-Fluvius grid
       * operator: 'offtake' / 'injection' end one direction only (Atrias-side
       * SC_COMMOFF / SC_COMMINJ). OMIT for Both or when not applicable —
       * omitted = whole-EAN end (previous behavior, SC_OFFINJE).
       */
      direction?: 'offtake' | 'injection' | 'both';
    }
  | {
      contractType: 'flex';
      company_id: number;
      contract_flex_id: number;
      effectiveDate: string; // YYYY-MM-DD
    };

export interface CommodityEndOfContractResult {
  requestId: string;
  ean: string;
  endDate: string;
  status: string;
}

/**
 * Flex success (BE contract revised 2026-07-08): flex endings no longer
 * write a mig6_eoc_requests row (that table is Atrias-only now), so there is
 * NO requestId and NO ean — the backend sets contracts_flex.end_date and
 * echoes it back.
 */
export interface FlexEndOfContractResult {
  contractType: 'flex';
  contractFlexId: number;
  endDate: string; // echoes effectiveDate verbatim, YYYY-MM-DD
  status: 'scheduled';
}

export interface EndOfContractResponse {
  data: CommodityEndOfContractResult | FlexEndOfContractResult;
}

/** Backend expects a bare 18-digit EAN (no "EAN" prefix). */
export const normalizeEan = (ean: string): string => ean.replace(/^EAN/i, '').trim();

/**
 * Latest end-of-contract request per EAN (commodity-only by construction —
 * the backend hard-filters flex log rows out).
 */
export interface EndOfContractRequestRow {
  request_id: string;
  ean: string;
  status: 'pending' | 'approved' | 'rejected';
  end_date: string | null;
  created_at: string;
  /**
   * Atrias service component of the request: SC_COMMOFF → offtake-only
   * pending, SC_COMMINJ → injection-only pending, anything else (incl.
   * null on legacy rows — DB column is nullable) → whole-EAN.
   */
  service_component: string | null;
}

export const fetchEndOfContractRequests = async (
  companyId: number,
  token: string
): Promise<EndOfContractRequestRow[]> => {
  const url = `${config.API_SERVICES}/api/v1/sdp/end-of-contract/${companyId}`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new EndOfContractError(
      response.status,
      `Failed to load end-of-contract requests: ${response.status}`
    );
  }

  const body = (await response.json()) as { data?: EndOfContractRequestRow[] };
  return body.data ?? [];
};

export const sendEndOfContractRequest = async (
  body: EndOfContractRequestBody,
  token: string
): Promise<EndOfContractResponse> => {
  const url = `${config.API_SERVICES}/api/v1/sdp/end-of-contract`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(
      body.contractType === 'commodity'
        ? { ...body, ean: normalizeEan(body.ean) }
        : body
    ),
  });

  if (!response.ok) {
    let message = `Failed to send end-of-contract request: ${response.status}`;
    let code: EndOfContractErrorCode | null = null;
    try {
      const errBody = (await response.json()) as { error?: string; code?: string };
      if (typeof errBody.error === 'string' && errBody.error) message = errBody.error;
      if (typeof errBody.code === 'string' && errBody.code) {
        code = errBody.code as EndOfContractErrorCode;
      }
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new EndOfContractError(response.status, message, code);
  }

  return response.json();
};
